/**
 * The two BLOCKING refusals on the RFQ handoff, explained in construction
 * language rather than as errors.
 *
 * Both exist to protect the integrity of the quote lineage, and neither has a
 * client-side bypass — offering a "prepare anyway" would defeat the whole
 * point of the refusal.
 *
 * IDENTITY REVIEW REQUIRED (422)
 *   OMNΛS holds more than one confirmed record that could be this firm, and
 *   nothing names which. Every candidate is reported and NONE is chosen — the
 *   same doctrine the Provider Passport applies to conflicting facts. React
 *   must not pick one, fuzzy-match, prefer a source, or merge identities.
 *
 * DUPLICATE PROVIDER LINEAGE (409)
 *   The same real organization would enter this package twice through two
 *   different commercial paths. Collapsing them would rewrite what a bidder
 *   actually submitted, so the second path is refused.
 */
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { recordTypeLabel, conflictFieldLabel, shortId } from "./exchangePresentation";
import { IdCard, GitMerge, AlertTriangle } from "lucide-react";

const RfqBlockerDialog = ({ blocker, onClose, onOpenProviders }) => {
  if (!blocker) return null;
  const isIdentity = blocker.kind === "identity-review";

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isIdentity ? (
              <IdCard className="w-5 h-5 text-amber-400" />
            ) : (
              <GitMerge className="w-5 h-5 text-amber-400" />
            )}
            {blocker.title}
          </DialogTitle>
          <DialogDescription>{blocker.explanation}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {isIdentity && (
            <>
              {blocker.candidates.length > 0 ? (
                <div className="space-y-1.5">
                  <p className="text-xs font-semibold text-muted-foreground">
                    Records that could be this firm
                    {blocker.candidateCount > blocker.candidates.length && (
                      <span className="font-normal">
                        {" "}
                        · showing {blocker.candidates.length} of {blocker.candidateCount}
                      </span>
                    )}
                  </p>
                  {/* A candidate arrives in one of TWO shapes: a record pair
                      {recordType, recordId}, or — when a listing's two source
                      refs are confirmed to DIFFERENT organizations — a field
                      pointer {field, value}. Reading only the first renders
                      "Linked record · undefined" for the case that matters
                      most, since that IS the conflict being reported. */}
                  {blocker.candidates.map((candidate, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between gap-2 rounded bg-muted/30 px-2 py-1.5"
                    >
                      {candidate.recordType ? (
                        <span className="text-xs" title={candidate.recordId}>
                          {recordTypeLabel(candidate.recordType)}
                          <span className="text-muted-foreground">
                            {" "}
                            · {shortId(candidate.recordId)}
                          </span>
                        </span>
                      ) : (
                        <span className="text-xs" title={candidate.value}>
                          {conflictFieldLabel(candidate.field)}
                          <span className="text-muted-foreground">
                            {" "}
                            · {shortId(candidate.value)}
                          </span>
                        </span>
                      )}
                    </div>
                  ))}
                  {/* A silently cut candidate list is not acceptable — a human
                      is choosing from it. */}
                  {blocker.candidatesTruncated && (
                    <p className="text-[11px] text-amber-400 flex items-start gap-1">
                      <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                      More candidates exist than are listed here.
                    </p>
                  )}
                  <p className="text-[11px] text-muted-foreground">
                    OMNΛS lists them and chooses none. Resolve the identity links so one record
                    is confirmed for this organization, or shortlist the specific record you
                    mean.
                  </p>
                </div>
              ) : (
                /* Four different refusals arrive with an empty candidate list
                   and they do not share one cause, so inventing a diagnosis
                   here would be wrong most of the time. The backend's own
                   message below IS the whole answer in this case. */
                <p className="text-xs text-muted-foreground">
                  OMNΛS reported no candidate records to choose between. The reason is below.
                </p>
              )}
            </>
          )}

          {!isIdentity && (
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-muted-foreground">
                The two commercial paths
              </p>
              <div className="space-y-1">
                <div className="flex items-center gap-2 rounded bg-muted/30 px-2 py-1.5">
                  <Badge variant="outline" className="font-normal text-[10px] shrink-0">
                    Already on the package
                  </Badge>
                  <span className="text-xs font-mono break-all">{blocker.existingBidderKey}</span>
                </div>
                <div className="flex items-center gap-2 rounded bg-muted/30 px-2 py-1.5">
                  <Badge variant="outline" className="font-normal text-[10px] shrink-0">
                    This attempt
                  </Badge>
                  <span className="text-xs font-mono break-all">{blocker.resolvedBidderKey}</span>
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground">
                The way forward is to resolve the identity links so the firm has one commercial
                record, or to withdraw the earlier preparation — not to prepare this one again.
              </p>
            </div>
          )}

          {/* The backend's own sentence, kept verbatim: it names the specific
              records and entries involved, which our copy deliberately does not
              try to restate. */}
          {blocker.backendMessage && (
            <p className="text-[11px] text-muted-foreground border-l-2 border-border pl-2">
              {blocker.backendMessage}
            </p>
          )}

          <div className="flex flex-wrap justify-end gap-2">
            {/* Both refusals are resolved the same way — by deciding identity
                links — and the provider directory is where that happens. The
                label names the destination it actually reaches. */}
            {onOpenProviders && (
              <Button variant="outline" size="sm" onClick={onOpenProviders}>
                <IdCard className="w-3.5 h-3.5 mr-1.5" />
                Resolve identity links
              </Button>
            )}
            <Button size="sm" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RfqBlockerDialog;
